import { formatDate, formatDateTime } from "@/lib/utils";
import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  CheckCircle,
  XCircle,
  Package,
  Eye,
  AlertCircle,
  MoreVertical,
  Loader2,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Modal from "@/components/ui/modal";
import RecordNotFound from "@/core/components/RecordNotFound";

const PendingApprovals = ({ isTab = false }) => {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginationMeta, setPaginationMeta] = useState({});
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: () => { },
  });

  const [inputDialog, setInputDialog] = useState({
    isOpen: false,
    title: "",
    description: "",
    label: "",
    placeholder: "",
    value: "",
    onConfirm: () => { },
    isRequired: false,
  });

  const currentStore = useSelector((state) => state.current_store);
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";
  const currentUser = useSelector((state) => state.user_data);

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const { data: pendingApprovals = [], isLoading } = useQuery({
    queryKey: [
      "pendingApprovals",
      currentStore?.id,
      currentPage,
      pageSize,
      searchText,
      hasPermission("stock adjustment view_all"),
    ],
    queryFn: async () => {
      const params = {
        store_id: currentStore?.id || null,
        page: currentPage,
        per_page: pageSize,
        search: searchText,
      };

      if (
        !hasPermission("stock adjustment view_all") &&
        hasPermission("stock adjustment view_own")
      ) {
        params.user_id = currentUser?.id;
      }

      Object.keys(params).forEach((key) => {
        if (
          params[key] === null ||
          params[key] === undefined ||
          params[key] === ""
        ) {
          delete params[key];
        }
      });

      const res = await apiClient.get(API_ENDPOINTS.STOCK.PENDING_APPROVALS, {
        params,
      });

      if (res.data.meta) {
        setPaginationMeta(res.data.meta);
        return res.data.data || [];
      } else if (res.data.current_page) {
        const { data, ...meta } = res.data;
        setPaginationMeta(meta);
        return data || [];
      }
      return res.data.data || [];
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || "Failed to fetch pending approvals",
      );
    },
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  const approveMutation = useMutation({
    mutationFn: async ({ id, notes }) => {
      const payload = { action: "approve" };
      if (notes && notes.trim()) {
        payload.notes = notes.trim();
      }
      await apiClient.put(API_ENDPOINTS.STOCK.APPROVAL(id), payload);
    },
    onSuccess: () => {
      toast.success("Stock adjustment approved successfully");
      queryClient.invalidateQueries(["pendingApprovals"]);
      setShowViewModal(false);
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to approve stock adjustment",
      );
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, notes }) => {
      const payload = {
        action: "reject",
        rejection_reason: notes.trim(),
      };
      await apiClient.put(API_ENDPOINTS.STOCK.APPROVAL(id), payload);
    },
    onSuccess: () => {
      toast.success("Stock adjustment rejected");
      queryClient.invalidateQueries(["pendingApprovals"]);
      setShowViewModal(false);
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to reject stock adjustment",
      );
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async ({ id, reason }) => {
      const payload = {};
      if (reason && reason.trim()) {
        payload.reason = reason.trim();
      }
      await apiClient.post(API_ENDPOINTS.STOCK.CANCEL(id), payload);
    },
    onSuccess: () => {
      toast.success("Stock adjustment cancelled successfully");
      queryClient.invalidateQueries(["pendingApprovals"]);
      setShowViewModal(false);
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to cancel stock adjustment",
      );
    },
  });

  const handleApprove = (id, adjustment_number) => {
    setInputDialog({
      isOpen: true,
      title: "Approve Adjustment?",
      description: `Are you sure you want to approve ${adjustment_number}?`,
      label: "Approval Notes (Optional)",
      placeholder: "Enter any approval notes...",
      value: "",
      onConfirm: (notes) => {
        approveMutation.mutate({ id, notes });
      },
      isRequired: false,
    });
  };

  const handleReject = (id, adjustment_number) => {
    setInputDialog({
      isOpen: true,
      title: "Reject Adjustment?",
      description: `Are you sure you want to reject ${adjustment_number}?`,
      label: "Rejection Reason (Required)",
      placeholder: "Enter reason for rejection...",
      value: "",
      onConfirm: (notes) => {
        rejectMutation.mutate({ id, notes });
      },
      isRequired: true,
    });
  };

  const handleCancel = (id, adjustment_number) => {
    setInputDialog({
      isOpen: true,
      title: "Cancel Adjustment?",
      description: `Are you sure you want to cancel ${adjustment_number}? You won't be able to revert this!`,
      label: "Cancellation Reason (Optional)",
      placeholder: "Enter reason for cancellation...",
      value: "",
      onConfirm: (reason) => {
        cancelMutation.mutate({ id, reason });
      },
      isRequired: false,
    });
  };

  const handleView = async (id) => {
    try {
      const res = await apiClient.get(API_ENDPOINTS.STOCK.ADJUSTMENT_DETAILS(id), {
        params: {
          with: "store,items.product,items.inventory,creator,approver,processor",
        },
      });

      if (res.status === 200) {
        setSelectedAdjustment(res.data.data);
        setShowViewModal(true);
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to load adjustment details",
      );
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "adjustment_number",
        header: "Adjustment #",
        cell: ({ row }) => (
          <span className="font-medium text-primary">
            {row.original.adjustment_number}
          </span>
        ),
      },
      {
        accessorKey: "store",
        header: "Store",
        cell: ({ row }) => (
          <span className="text-sm">{row.original.store?.name || "-"}</span>
        ),
      },
      {
        accessorKey: "reason_label",
        header: "Reason",
        cell: ({ row }) => {
          const record = row.original;

          if (!hasPermission("stock adjustment approve")) {
            return <RecordNotFound />;
          }
          return (
            <div>
              <div className="font-medium">{record.reason_label}</div>
              {record.notes && (
                <div className="text-xs text-muted-foreground mt-1">
                  {record.notes.length > 50
                    ? `${record.notes.substring(0, 50)}...`
                    : record.notes}
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "total_items",
        header: "Items",
        cell: ({ row }) => (
          <Badge variant="outline" className="font-normal">
            {row.original.total_items}
          </Badge>
        ),
      },
      {
        accessorKey: "total_adjustment_value",
        header: "Adjustment Value",
        cell: ({ row }) => {
          const value = parseFloat(row.original.total_adjustment_value);
          const isPositive = row.original.is_positive_adjustment;
          return (
            <div className="flex items-center gap-1">
              {isPositive ? (
                <TrendingUp className="h-4 w-4 text-green-600" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-600" />
              )}
              <span
                className={`font-semibold ${isPositive ? "text-green-600" : "text-red-600"}`}
              >
                {isPositive ? "+" : "-"}
                {currencySymbol}
                {Math.abs(value).toFixed(2)}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "created_by",
        header: "Created By",
        cell: ({ row }) => {
          const creator = row.original.creator;
          return (
            <span className="text-sm">
              {creator?.employee?.full_name || creator?.username || "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        cell: ({ row }) => (
          <span className="text-sm">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleView(row.original.id)}>
                <Eye className="mr-2 h-4 w-4" /> View Details
              </DropdownMenuItem>
              {hasPermission("stock adjustment approve") && (
                <>
                  <DropdownMenuItem
                    onClick={() =>
                      handleApprove(
                        row.original.id,
                        row.original.adjustment_number,
                      )
                    }
                    disabled={
                      approveMutation.isPending ||
                      rejectMutation.isPending ||
                      cancelMutation.isPending
                    }
                  >
                    <CheckCircle className="mr-2 h-4 w-4" /> Approve
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() =>
                      handleReject(
                        row.original.id,
                        row.original.adjustment_number,
                      )
                    }
                    disabled={
                      approveMutation.isPending ||
                      rejectMutation.isPending ||
                      cancelMutation.isPending
                    }
                  >
                    <XCircle className="mr-2 h-4 w-4" /> Reject
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() =>
                      handleCancel(
                        row.original.id,
                        row.original.adjustment_number,
                      )
                    }
                    disabled={
                      approveMutation.isPending ||
                      rejectMutation.isPending ||
                      cancelMutation.isPending
                    }
                  >
                    <XCircle className="mr-2 h-4 w-4" /> Cancel
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currencySymbol,
      hasPermission,
      approveMutation.isPending,
      rejectMutation.isPending,
    ],
  );

  const content = (
    <>
      {!isTab && (
        <PageHeader>
          <PageHeaderHeading
            title="Pending Approvals"
            subtitle="Stock Adjustments Awaiting Approval"
          />
        </PageHeader>
      )}

      {pendingApprovals.length === 0 && !isLoading && (
        <Alert className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No Pending Approvals</AlertTitle>
          <AlertDescription>
            There are currently no stock adjustments waiting for approval.
          </AlertDescription>
        </Alert>
      )}

      <FilterCard>
        <FilterSearch
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search pending approvals..."
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={pendingApprovals}
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          pageCount={paginationMeta.last_page || 1}
          onPaginationChange={(updater) => {
            if (typeof updater === "function") {
              const newState = updater({
                pageIndex: currentPage - 1,
                pageSize,
              });
              setCurrentPage(newState.pageIndex + 1);
              setPageSize(newState.pageSize);
            }
          }}
          isLoading={isLoading}
          onRowClick={(row) => handleView(row.id)}
          enableRowSelection={true}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Pending: {paginationMeta.total || 0}
            </span>
          }
        />
      </TableCard>
    </>
  );

  return (
    <>
      {isTab ? (
        content
      ) : (
        <div className="page-wrapper">
          <div className="content p-6 max-w-[100vw] overflow-x-hidden">
            {content}
          </div>
        </div>
      )}

      {/* View Details Modal */}
      <Modal
        open={showViewModal}
        onOpenChange={setShowViewModal}
        title={`Pending Approval - ${selectedAdjustment?.adjustment_number || ""}`}
        icon={Package}
        maxWidth="max-w-5xl"
      >
        {selectedAdjustment && (
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Adjustment Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Adjustment #:</span>
                    <span className="font-medium">
                      {selectedAdjustment.adjustment_number}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <Badge variant="warning" className="capitalize">
                      Pending Approval
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Store:</span>
                    <span className="font-medium">
                      {selectedAdjustment.store?.name || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Reason:</span>
                    <span className="font-medium">
                      {selectedAdjustment.reason_label}
                    </span>
                  </div>
                  {[
                    "opening_balance",
                    "physical_count",
                    "found",
                    "customer_return",
                  ].includes(selectedAdjustment.reason) && (
                      <div className="text-[10px] text-blue-600 font-medium flex items-center gap-1 mt-1 justify-end">
                        <AlertCircle className="h-3 w-3" />
                        Auto-processes after approval
                      </div>
                    )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Created By:</span>
                    <span className="font-medium">
                      {selectedAdjustment.creator?.employee?.full_name ||
                        selectedAdjustment.creator?.username ||
                        "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Created At:</span>
                    <span className="font-medium">
                      {formatDateTime(selectedAdjustment.created_at)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Items:</span>
                    <span className="font-medium">
                      {selectedAdjustment.total_items}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Adjustment Value:
                    </span>
                    <span
                      className={`font-bold ${selectedAdjustment.is_positive_adjustment ? "text-green-600" : "text-red-600"}`}
                    >
                      {selectedAdjustment.is_positive_adjustment ? "+" : "-"}
                      {currencySymbol}
                      {Math.abs(
                        parseFloat(selectedAdjustment.total_adjustment_value),
                      ).toFixed(2)}
                    </span>
                  </div>
                  {selectedAdjustment.notes && (
                    <div className="pt-2 border-t">
                      <span className="text-muted-foreground block mb-1">
                        Notes:
                      </span>
                      <p className="text-sm">{selectedAdjustment.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {selectedAdjustment.items &&
              selectedAdjustment.items.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      Adjustment Items
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-md border overflow-x-auto">
                      <ShadcnTable>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>Product</TableHead>
                            <TableHead className="text-right">
                              Current Qty
                            </TableHead>
                            <TableHead className="text-right">
                              Adjustment
                            </TableHead>
                            <TableHead className="text-right">
                              New Qty
                            </TableHead>
                            <TableHead className="text-right">
                              Unit Cost
                            </TableHead>
                            <TableHead className="text-right">
                              Total Value
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedAdjustment.items.map((item, index) => {
                            const adjustment = parseFloat(
                              item.adjustment_quantity || 0,
                            );
                            const current = parseFloat(
                              item.current_quantity || 0,
                            );
                            const newQty = current + adjustment;
                            const unitCost = parseFloat(item.unit_cost || 0);
                            const totalValue = Math.abs(adjustment * unitCost);

                            return (
                              <TableRow key={item.id}>
                                <TableCell className="font-medium">
                                  {index + 1}
                                </TableCell>
                                <TableCell>
                                  <div>
                                    <div className="font-medium">
                                      {item.product?.name || "Unknown Product"}
                                    </div>
                                    {item.reason_notes && (
                                      <div className="text-xs text-muted-foreground mt-1">
                                        {item.reason_notes}
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">
                                  {current}
                                </TableCell>
                                <TableCell
                                  className={`text-right font-semibold ${adjustment >= 0 ? "text-green-600" : "text-red-600"}`}
                                >
                                  {adjustment >= 0 ? "+" : ""}
                                  {adjustment}
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                  {newQty}
                                </TableCell>
                                <TableCell className="text-right">
                                  {currencySymbol}
                                  {unitCost.toFixed(2)}
                                </TableCell>
                                <TableCell className="text-right font-semibold">
                                  {currencySymbol}
                                  {totalValue.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </ShadcnTable>
                    </div>
                  </CardContent>
                </Card>
              )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              {hasPermission("stock adjustment approve") && (
                <>
                  <Button
                    variant="outline"
                    onClick={() =>
                      handleCancel(
                        selectedAdjustment.id,
                        selectedAdjustment.adjustment_number,
                      )
                    }
                    disabled={
                      approveMutation.isPending ||
                      rejectMutation.isPending ||
                      cancelMutation.isPending
                    }
                    className="gap-2 text-destructive"
                  >
                    {cancelMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    <XCircle className="h-4 w-4" />
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      handleReject(
                        selectedAdjustment.id,
                        selectedAdjustment.adjustment_number,
                      )
                    }
                    disabled={
                      approveMutation.isPending ||
                      rejectMutation.isPending ||
                      cancelMutation.isPending
                    }
                    className="gap-2"
                  >
                    {rejectMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    <XCircle className="h-4 w-4 text-destructive" />
                    Reject
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      handleApprove(
                        selectedAdjustment.id,
                        selectedAdjustment.adjustment_number,
                      )
                    }
                    disabled={
                      approveMutation.isPending ||
                      rejectMutation.isPending ||
                      cancelMutation.isPending
                    }
                    className="gap-2"
                  >
                    {approveMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    <CheckCircle className="h-4 w-4" />
                    Approve
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Confirmation Dialog */}
      <Modal
        open={confirmDialog.isOpen}
        onOpenChange={(open) =>
          setConfirmDialog({ ...confirmDialog, isOpen: open })
        }
        title={confirmDialog.title}
        footer={
          <div className="flex justify-end gap-3 -mx-2">
            <Button
              variant="outline"
              onClick={() =>
                setConfirmDialog({ ...confirmDialog, isOpen: false })
              }
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                confirmDialog.onConfirm();
                setConfirmDialog({ ...confirmDialog, isOpen: false });
              }}
            >
              Continue
            </Button>
          </div>
        }
      >
        <div className="py-4">
          <p className="text-sm text-muted-foreground font-medium">
            {confirmDialog.description}
          </p>
        </div>
      </Modal>

      {/* Action Input Dialog */}
      <Modal
        open={inputDialog.isOpen}
        onOpenChange={(open) =>
          setInputDialog({ ...inputDialog, isOpen: open })
        }
        title={inputDialog.title}
        maxWidth="max-w-lg"
        footer={
          <div className="flex justify-end gap-3 -mx-2">
            <Button
              variant="outline"
              onClick={() => setInputDialog({ ...inputDialog, isOpen: false })}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              disabled={inputDialog.isRequired && !inputDialog.value.trim()}
              onClick={() => {
                inputDialog.onConfirm(inputDialog.value);
                setInputDialog({ ...inputDialog, isOpen: false });
              }}
            >
              Proceed
            </Button>
          </div>
        }
      >
        <div className="py-4 space-y-4">
          {inputDialog.description && (
            <p className="text-sm text-muted-foreground font-medium">
              {inputDialog.description}
            </p>
          )}
          <div className="space-y-2">
            <Label>{inputDialog.label}</Label>
            <Textarea
              placeholder={inputDialog.placeholder}
              value={inputDialog.value}
              onChange={(e) =>
                setInputDialog({ ...inputDialog, value: e.target.value })
              }
              className="min-h-[100px]"
              autoFocus
            />
            {inputDialog.isRequired && !inputDialog.value.trim() && (
              <p className="text-[10px] text-destructive">
                This field is required.
              </p>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PendingApprovals;
